import React, { useEffect, useState, createContext, useContext } from 'react';
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  signUp: (email: string) => Promise<void>;
  completeRegistration: (userData: Partial<User>) => Promise<void>;
}
interface User {
  email: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  role?: string;
  hasInvitedTeam?: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // Initialize state from localStorage if available
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  // Update localStorage when auth state changes
  useEffect(() => {
    localStorage.setItem('isAuthenticated', isAuthenticated.toString());
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [isAuthenticated, user]);
  const login = async (email: string, password: string) => {
    // This would normally make an API call to authenticate
    const userData = {
      email
    };
    setUser(userData);
    setIsAuthenticated(true);
  };
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
  };
  const signUp = async (email: string) => {
    // This would normally make an API call to register the email
    setUser({
      email
    });
    // Note: Not setting isAuthenticated here as registration is not complete
  };
  const completeRegistration = async (userData: Partial<User>) => {
    // This would normally make an API call to complete registration
    const updatedUser = {
      ...user,
      ...userData
    };
    setUser(updatedUser);
    setIsAuthenticated(true);
  };
  return <AuthContext.Provider value={{
    isAuthenticated,
    user,
    login,
    logout,
    signUp,
    completeRegistration
  }} data-id="element-23">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}